/**
 * 28 - The Defector (10 §2): the headquarters he is walking out of stands inside its wire; the rendezvous
 * is a ruin outside it. He is not there until the stated time - then he waits by the ruin to be met, and
 * follows. He is unarmed and no soldier: while rounds are flying he stays where he is. If the garrison is
 * alarmed (alert 2) before the two have met he has twenty seconds' nerve left, and then he is gone.
 *
 * His time is set by `finish` from the walk the finished map offers (helpers3bTime.ts).
 */

import type { Point } from '../types';
import { checker, clutter, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure } from './helpers';
import { clearing, FIRST_CAPTIVE_ID, flagTargets, keepClear, openNorth, RECALL, standNear } from './helpers3b';
import { carefulSeconds, fairTimer, timedRule } from './helpers3bTime';
import type { ArchetypeSpec } from './types';

/** Seconds of nerve he has left once the alarm is up: time to reach him if he is already waiting, no more. */
const NERVE = 20;
/** The rendezvous is out of sight of the wire by day (a soldier sees 22 m), but not a hike from it. */
const OFF_THE_WIRE = 24;
const NOT_A_HIKE = 60;

export const defector: ArchetypeSpec = {
  id: 'defector',
  name: 'THE DEFECTOR',
  nativeBiome: 'temperate',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 54], d: [28, 32] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    // The headquarters he is leaving: shelters, a guard house, the duty officer - none of it his business any more.
    const placed: Point[] = [];
    for (let i = 0; i < 3; i++) structure(g, g.kit.shelter, { minDist: 8, from: placed, margin: 1.4 });
    const house = required(g, 'guardHouse', { minDist: 8, from: placed, margin: 1.4 });
    structure(g, 'staffCar', { minDist: 6, from: placed, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    guardAtDoor(g, house, 'officer');
    guardsAbout(g, 2 + Math.floor(g.threat / 4));

    // The rendezvous: a ruin outside the wire, clear of the pickup and of the quiet way in.
    const clearOf = [{ at: g.ctx.plan.pickup, radius: 12 }];
    if (g.ctx.quiet.end) clearOf.push({ at: g.ctx.quiet.end, radius: 10 });
    const centre = { x: (g.rect.x0 + g.rect.x1) / 2, z: (g.rect.z0 + g.rect.z1) / 2 };
    const at = clearing(g, {
      within: openNorth(g), radius: 6, clearOf, outside: { rect: g.rect, pad: OFF_THE_WIRE }, near: centre, maxDist: NOT_A_HIKE,
    });
    if (!at) g.map.problems.push('defector: no ground for the rendezvous outside the wire');
    const where = at ?? { x: g.rect.x0 - 20, z: g.rect.z1 + 20 };
    const ruin = g.map.add('ruinHouse', where.x, where.z);
    g.map.place('ruinWall', where.x + 5.5, where.z - 1.5, { length: 3, margin: 0.2 });
    // He waits in the lee of the ruin, on the side away from the headquarters.
    const away = where.z >= centre.z ? 1 : -1;
    const spot = standNear(g, where.x, where.z + away * 3.6, 3, true);
    if (!spot) g.map.problems.push('defector: nowhere at the rendezvous for him to wait');
    const waits = { x: spot?.[0] ?? where.x, z: spot?.[1] ?? where.z + away * 4 };
    g.map.captives.push({ id: FIRST_CAPTIVE_ID, kind: 'defector', ...waits });
    keepClear(g, waits, 5);

    return {
      objective: {
        kind: 'freeAndEscort', label: 'BRING OUT THE DEFECTOR', targetPropIds: [ruin.id], minFraction: 1,
        useLabel: 'MEET THE DEFECTOR', targetName: 'THE DEFECTOR',
        rules: [
          {
            type: 'timedEvent', atSeconds: 300, effect: 'contactArrives',
            orders: 'THE DEFECTOR COMES TO THE RUIN AT THE STATED TIME, NOT BEFORE: MEET HIM THERE AND WALK HIM OUT',
            warning: 'THE DEFECTOR IS DUE AT THE RENDEZVOUS IN',
            reason: 'THE DEFECTOR DID NOT COME',
          },
          {
            type: 'alertConsequence', level: 2, delaySeconds: NERVE, effect: 'contactAborts',
            orders: 'IF THE ALARM IS RAISED BEFORE YOU HAVE MET HIM, HE LOSES HIS NERVE AND IS GONE',
            warning: 'THE DEFECTOR IS LOSING HIS NERVE — GONE IN',
            reason: 'THE ALARM SCARED THE DEFECTOR OFF',
          },
          RECALL,
        ],
      },
      finish(def) {
        flagTargets(def, [ruin.id]);
        const rule = timedRule(def, 'contactArrives');
        if (rule) rule.atSeconds = carefulSeconds(def, def.player, waits);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('defector');
    const o = def.objective;
    const [man] = def.captives;
    check(o.kind === 'freeAndEscort' && o.targetName === 'THE DEFECTOR', 'objective must be freeAndEscort, and name the defector');
    check(def.captives.length === 1 && man?.kind === 'defector', 'needs exactly one captive, the defector');
    const comes = timedRule(def, 'contactArrives');
    check(comes !== undefined, 'he must arrive with a contactArrives event: without one he would be waiting from the start');
    const aborts = ruleOf(def, 'alertConsequence');
    check(aborts?.effect === 'contactAborts' && aborts.level === 2, 'alert 2 before the meeting must lose him');
    for (const rule of [comes, aborts]) {
      check(
        rule?.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
        'every rule must carry its orders, its countdown and its reason',
      );
    }
    const ruin = def.props.find((p) => p.id === o.targetPropIds[0]);
    check(ruin?.kind === 'ruinHouse' && ruin.target === true, 'the rendezvous must be marked on the strip map');
    if (man) {
      check(!insideRect(def.goalZone, man.x, man.z), 'the rendezvous must lie outside the wire');
      check(probe.standable({ x0: man.x - 0.6, z0: man.z - 0.6, x1: man.x + 0.6, z1: man.z + 0.6 }) >= 1, 'he must wait on open ground');
      check(ruin !== undefined && Math.abs(ruin.x - man.x) <= 7 && Math.abs(ruin.z - man.z) <= 7, 'he must wait at the ruin');
      if (ruin) check(probe.breach(ruin.id) === 0, 'the rendezvous must be reachable without crossing mines or wire');
      check(fairTimer(def, comes, def.player, man), 'he must not come before a careful man can be at the rendezvous');
    }
    return out;
  },
};
